import { ActionMenu, Button, Select } from '@platform/design-system';
import { Pencil, SlidersHorizontal, Star, Trash2 } from 'lucide-react';
import { usePageContext } from '../../usePageContext.ts';
import type { DashboardView } from './widget-types.ts';
import styles from './Widgets.module.css';

const ICON_SIZE = 20;

export type ViewControlsProps = {
  views: readonly DashboardView[];
  active: DashboardView | undefined;
  editing: boolean;
  onSelect: (id: string | null) => void;
  onCustomize: () => void;
  onMakeDefault: (view: DashboardView) => void;
  onRename: (view: DashboardView) => void;
  onDelete: (view: DashboardView) => void;
};

export const ViewControls = ({
  views,
  active,
  editing,
  onSelect,
  onCustomize,
  onMakeDefault,
  onRename,
  onDelete,
}: ViewControlsProps) => {
  const { t } = usePageContext();
  return (
    <div className={styles['viewControls']}>
      <Select
        label={t('dashboard.views.label')}
        hideLabel
        disabled={editing}
        value={active?.id ?? ''}
        options={[
          { value: '', label: t('dashboard.views.standard') },
          ...views.map((view) => ({
            value: view.id,
            label: view.isDefault ? t('dashboard.views.defaultName', { name: view.name }) : view.name,
          })),
        ]}
        onChange={(event) => {
          onSelect(event.target.value === '' ? null : event.target.value);
        }}
      />
      {active === undefined || editing ? null : (
        <ActionMenu
          label={t('dashboard.views.options')}
          actions={[
            {
              key: 'default',
              label: t('dashboard.views.makeDefault'),
              icon: <Star size={ICON_SIZE} />,
              disabled: active.isDefault,
              onSelect: () => {
                onMakeDefault(active);
              },
            },
            {
              key: 'rename',
              label: t('dashboard.views.rename'),
              icon: <Pencil size={ICON_SIZE} />,
              onSelect: () => {
                onRename(active);
              },
            },
            {
              key: 'delete',
              label: t('dashboard.views.delete'),
              icon: <Trash2 size={ICON_SIZE} />,
              tone: 'danger',
              onSelect: () => {
                onDelete(active);
              },
            },
          ]}
        />
      )}
      {editing ? null : (
        <Button
          variant="outline"
          icon={<SlidersHorizontal size={ICON_SIZE} aria-hidden="true" />}
          onClick={onCustomize}
        >
          {t('dashboard.overview.customize')}
        </Button>
      )}
    </div>
  );
};
